<template>
  <bm-control v-bind="$attrs" :read-only-value="formattedDateRange">
    <template #default="{ dataQa }">
      <v-menu
        ref="menuComponentRef"
        v-model="menu"
        :close-on-content-click="false"
        :return-value="dateRange"
        transition="scale-transition"
        offset-y
        min-width="290px"
        @update:return-value="updateDateRangeFromControl"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="formattedDateRange"
            class="bm-text"
            :placeholder="placeholderVal"
            prepend-icon="date_range"
            readonly
            v-bind="attrs"
            :clearable="clearable"
            color="brand02"
            :label="undefined"
            :data-qa="dataQa"
            :disabled="disabled"
            :error-messages="validationErrors"
            v-on="on"
            @input="updateDateRangeFromControl"
          ></v-text-field>
        </template>
        <v-card :data-qa="`${dataQa}_wrapper`">
          <div class="d-flex">
            <div v-if="$vuetify.breakpoint.smAndUp" class="ml-2 mt-1">
              <v-list dense>
                <div class="preset-header">{{ $m.date_range_presets }}</div>
                <v-list-item-group
                  :value="currentPreset"
                  :data-qa="`${dataQa}_presets`"
                  @change="changePreset"
                >
                  <v-list-item
                    v-for="preset in presets"
                    :key="preset.key"
                    :value="preset.key"
                    :data-qa="preset.key"
                  >
                    <v-list-item-title>{{ preset.label }}</v-list-item-title>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </div>
            <div>
              <v-date-picker
                v-model="dateRange"
                no-title
                scrollable
                color="brand02"
                range
                :data-qa="`${dataQa}_picker`"
                :picker-date.sync="displayedPickerDate"
                :first-day-of-week="startDayOfWeek"
              >
                <div class="range-controls">
                  <div
                    v-if="$vuetify.breakpoint.xsOnly"
                    class="d-flex justify-center"
                  >
                    <bm-dropdown
                      class="preset_dropdown"
                      :value="currentPreset"
                      :attach="false"
                      :items="presets"
                      :searchable="false"
                      item-text="label"
                      item-value="key"
                      :placeholder="$m.date_range_presets_dropdown"
                      @input="changePreset"
                    />
                  </div>
                  <div class="d-flex">
                    <v-spacer />
                    <bm-btn
                      color="primary"
                      button-type="ghost"
                      :t="$m.keys.cancel"
                      @click="menu = false"
                    />
                    <bm-btn
                      button-type="ghost"
                      color="primary"
                      :t="$m.keys.ok"
                      @click="
                        menuComponentRef?.save(dateRange);
                        $emit('change', value);
                      "
                    />
                  </div>
                </div>

                <v-spacer></v-spacer>
              </v-date-picker>
            </div>
          </div>
        </v-card>
      </v-menu>
    </template>
  </bm-control>
</template>

<script lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import type { PropType } from "vue";
import { computed, defineComponent, ref, watch } from "vue";
import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { DateRangePreset } from "@/components/controls/daterange/dateRangePresets";
import allPresets from "@/components/controls/daterange/dateRangePresets";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import { useI18n } from "@/plugins/i18n";
import type { ValidationRule } from "@/plugins/validation";
import userStore from "@/store/modules/userStore";

/**
 * Removes duplicates localdates and sorts.
 */
function rangeSanitise(range: LocalDate[]): LocalDate[] {
  let newRange = [...new Set(range.map((date) => date.toString()))].map(
    (date) => LocalDate.parse(date)
  );
  newRange.sort((a, b) => (a.isBefore(b) ? -1 : 1));

  return newRange;
}

/**
 * Compares arrays of localdates for equality.
 */
function rangeEquals(a: LocalDate[], b: LocalDate[]): boolean {
  return (
    rangeSanitise(a)
      .map((date) => date.toString())
      .join("|") ===
    rangeSanitise(b)
      .map((date) => date.toString())
      .join("|")
  );
}

export const customPresetKey = "date_range_custom_preset_key";

export default defineComponent({
  name: "BmDateRange",
  components: { BmDropdown, BmControl },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    // TODO: [BREW-6019] Date range type should be more specific to allow for null values
    value: {
      type: Array as PropType<LocalDate[]>,
      required: true,
    },
    presets: {
      type: Array as PropType<DateRangePreset[]>,
      default: () => allPresets,
      required: false,
    },
    rules: {
      type: Array as PropType<ValidationRule[]>,
      default: () => [],
      required: false,
    },
    /** Return only property indicating the current preset key (value has no affect on the range control) **/
    returnPresetKey: {
      type: String,
      default: () => customPresetKey,
      required: false,
    },
    placeholder: {
      type: String,
    },
    clearable: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
  },
  setup(props, context) {
    const { $m } = useI18n();
    const menu = ref(false);
    const dateRange = ref<string[]>([]);
    const displayedPickerDate = ref(""); // YYYY-MM displayed month view in picker
    const currentPreset = ref<string>();
    const placeholderVal = computed(() => props.placeholder ?? " ");
    const validationErrors = ref<string[]>([]);
    const menuComponentRef = ref<{
      save: (dateRange: string[]) => void;
    } | null>(null);
    const formattedDateRange = computed(() => {
      const formattedDates = props.value.map((date) =>
        tenantService.formatDate(date)
      );

      // If both dates are the same, splice one of them for a prettier view
      if (formattedDates.length === 2) {
        if (formattedDates[0] === formattedDates[1]) {
          formattedDates.splice(0, 1);
        }
      }

      return formattedDates.join(` ${$m.date_range_to} `);
    });
    const startDayOfWeek = userStore.state.user?.calendarStartDay.key ?? 1;

    /**
     * Prop value watcher:
     * 1. Sync's the current preset value to the property value e.g. yesterday preset highlights if the range is yesterday.
     * 2. Pushes value updates into the date picker controls.
     */
    watch(
      () => props.value,
      () => {
        let localDateRange = rangeSanitise(props.value);

        // select a matching preset if one exists.

        const newPreset =
          props.presets.find((preset) =>
            rangeEquals(rangeSanitise(preset.getRange()), localDateRange)
          )?.key ?? customPresetKey;

        if (newPreset !== currentPreset.value) {
          currentPreset.value = newPreset;
          context.emit("update:return-preset-key", newPreset);
        }

        dateRange.value = props.value.map((localDate) => localDate.toString());
      },
      { immediate: true }
    );

    /**
     * Sync the displayed picker to the current value when opening the picker.
     */
    watch(
      () => menu.value,
      () => {
        if (menu.value) {
          displayedPickerDate.value = rangeSanitise(props.value)[0]?.format(
            DateTimeFormatter.ofPattern("yyyy-MM")
          );
        }
      }
    );

    /**
     * User initiated preset change - update values and move displayed picker month to start of range.
     */
    const changePreset = (presetKey: string) => {
      const foundPreset = props.presets.find(
        (preset) => preset.key === presetKey
      );
      if (foundPreset) {
        displayedPickerDate.value = foundPreset
          .getRange()[0]
          .format(DateTimeFormatter.ofPattern("yyyy-MM"));
        dateRange.value = foundPreset.getRange().map((date) => date.toString());
      }
    };
    /**
     * User initiated date range picker updates come via this function. Also clearable event changes
     */
    const updateDateRangeFromControl = (dateRangeStrings: string[] | null) => {
      updateDateRange(
        dateRangeStrings?.map((localDate) => LocalDate.parse(localDate)) ?? []
      );
    };

    /**
     * Vuetify still calls update on cancel, so we should check if our values are the same
     * and only emit an update if they are
     */
    const updateDateRange = (range: LocalDate[]) => {
      validationErrors.value = [];
      const sortedPropsDates = rangeSanitise([...props.value]);
      const sortedSelectedRange = rangeSanitise(range);

      dateRange.value = sortedSelectedRange.map((localDate) =>
        localDate.toString()
      );

      if (!rangeEquals(sortedSelectedRange, sortedPropsDates)) {
        if (props.rules.length > 0) {
          validationErrors.value = props.rules
            .map((rule) => rule(sortedSelectedRange))
            .filter((error) => typeof error === "string") as string[];
          if (validationErrors.value.length > 0) {
            return;
          }
        }
        context.emit("input", sortedSelectedRange);
      }
    };

    return {
      menu,
      dateRange,
      updateDateRangeFromControl,
      currentPreset,
      formattedDateRange,
      placeholderVal,
      displayedPickerDate,
      changePreset,
      validationErrors,
      menuComponentRef,
      startDayOfWeek,
    };
  },
});
</script>

<style scoped lang="sass">
@import "~@/sass/_typography"

.range-controls
  width: 100%

.preset_dropdown
  width: 75%

:deep(.v-list-item--dense), .v-list--dense .v-list-item
  min-height: 32px

.preset-header
  @include bm-body2
  font-weight: bold
  margin-top: 3px
  margin-left: 8px
  margin-bottom: 14px
</style>
